/**
 * Typed fetch client for /api/*. Single network entrypoint for the SPA.
 *
 * - Prefixes /api/ on all requests.
 * - Sets `x-csrf: 1` on all mutating verbs (ARCH §8.3 / AC-136).
 * - Parses the success envelope (raw JSON) OR throws an `ApiError` carrying
 *   the closed error code from the response envelope.
 * - Always includes credentials so the `forge_sid` HttpOnly cookie travels.
 */
import type { ApiErrorBody, ApiErrorCode } from '@shared/types';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export class ApiError extends Error {
  readonly code: ApiErrorCode | string;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(opts: {
    code: ApiErrorCode | string;
    message: string;
    status: number;
    details?: Record<string, unknown>;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.code = opts.code;
    this.status = opts.status;
    this.details = opts.details;
  }
}

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null | string[]>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Bypass JSON parsing — useful for 204 No Content responses. Default false.
   */
  raw?: boolean;
}

function buildQuery(query?: ApiFetchOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.append(key, String(value));
    }
  }
  const str = params.toString();
  return str ? `?${str}` : '';
}

/**
 * Generic typed API fetch. T is the success response shape — pass `void`
 * for 204 endpoints.
 */
export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.headers ?? {}),
  };
  if (MUTATING_METHODS.has(method)) {
    headers['x-csrf'] = '1';
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json; charset=utf-8';
  }

  const url = `/api${path.startsWith('/') ? path : `/${path}`}${buildQuery(options.query)}`;

  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  if (options.signal) {
    init.signal = options.signal;
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    // Network / abort / offline. We surface it as an opaque INTERNAL so the
    // UI can react with the editorial "try again" copy without leaking the
    // raw error.
    throw new ApiError({
      code: 'INTERNAL',
      message:
        error instanceof Error && error.name === 'AbortError'
          ? 'Request aborted.'
          : "Couldn't reach the server.",
      status: 0,
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!response.ok) {
    let body: ApiErrorBody | undefined;
    try {
      body = text ? (JSON.parse(text) as ApiErrorBody) : undefined;
    } catch {
      body = undefined;
    }
    throw new ApiError({
      code: body?.error?.code ?? `HTTP_${response.status}`,
      message: body?.error?.message ?? 'Something went wrong on our side.',
      status: response.status,
      ...(body?.error?.details ? { details: body.error.details } : {}),
    });
  }

  if (options.raw) {
    return text as unknown as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new ApiError({
      code: 'INTERNAL',
      message: "The server returned a response we couldn't parse.",
      status: response.status,
    });
  }
}
